<!--
Copyright (C) 2025 Checkmk GmbH - License: GNU General Public License v2
This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
conditions defined in the file COPYING, which is part of this source code package.
-->
<script setup lang="ts">
import usei18n from '@/lib/i18n'

import CmkCatalogPanel from '@/components/CmkCatalogPanel.vue'

import DashboardPreviewContent from '@/dashboard/components/DashboardPreviewContent.vue'
import ContentSpacer from '@/dashboard/components/Wizard/components/ContentSpacer.vue'
import WidgetVisualization from '@/dashboard/components/Wizard/components/WidgetVisualization/WidgetVisualization.vue'
import type { BaseWidgetProp, WidgetProps } from '@/dashboard/components/Wizard/types'
import DataSettings from '@/dashboard/components/Wizard/wizards/other/stage1/DataSettings.vue'
import type { GetValidWidgetProps } from '@/dashboard/components/Wizard/wizards/other/types'
import type { WidgetSpec } from '@/dashboard/types/widget'

import SidebarElementSelector from './SidebarElementSelector.vue'
import { useSidebarElements } from './composables/useSidebarElements'
import { useSidebarWidget } from './composables/useSidebarWidget'

const { _t } = usei18n()
interface Props extends BaseWidgetProp {
  editWidgetSpec: WidgetSpec | null
}
const props = defineProps<Props>()
const sidebarElements = useSidebarElements()
const handler = useSidebarWidget(
  sidebarElements.elements,

  props.editWidgetSpec
)

function getValidWidgetProps(): WidgetProps | null {
  const validSidebarElement = sidebarElements.elements.value.some(
    (el) => el.id === handler.sidebarElementName.value
  )
  if (validSidebarElement && handler.validate()) {
    return handler.widgetProps.value
  }
  return null
}
defineExpose<GetValidWidgetProps>({ getValidWidgetProps })
</script>

<template>
  <div>
    <DashboardPreviewContent
      widget_id="sidebar-element-preview"
      :dashboard-key="dashboardKey"
      :general_settings="handler.widgetProps.value.general_settings!"
      :content="handler.widgetProps.value.content!"
      :effective-title="handler.widgetProps.value!.effectiveTitle"
      :effective_filter_context="handler.widgetProps.value.effective_filter_context!"
      :content-center="true"
    />

    <ContentSpacer />

    <DataSettings :label="_t('Select sidebar element')">
      <SidebarElementSelector
        v-model:selected-sidebar-element="handler.sidebarElementName.value"
        :elements="sidebarElements.elements.value"
        :is-loading="sidebarElements.isLoading.value"
        :has-error="sidebarElements.hasError.value"
      />
    </DataSettings>

    <ContentSpacer :dimension="6" />

    <CmkCatalogPanel :title="_t('Widget settings')" variant="padded">
      <WidgetVisualization
        v-model:show-title="handler.showTitle.value"
        v-model:show-title-background="handler.showTitleBackground.value"
        v-model:show-widget-background="handler.showWidgetBackground.value"
        v-model:title="handler.title.value"
        v-model:title-url="handler.titleUrl.value"
        v-model:title-url-enabled="handler.titleUrlEnabled.value"
        v-model:title-url-validation-errors="handler.titleUrlValidationErrors.value"
        :title-macros="handler.titleMacros.value"
      />
    </CmkCatalogPanel>

    <ContentSpacer />
  </div>
</template>
